export async function checkStatus(url, options = {}) {
  const {
    timeoutMs = 6000,
    maxRedirects = 5,
    method = 'GET',
    enableDNS = false,
    thresholdMs = 2000
  } = options;

  const WORKER_URL = 'https://wds-worker.blogging-rajesh.workers.dev/'; // TODO: Replace with your worker URL

  // Try worker proxy as fallback
  try {
    return await checkViaProxy(WORKER_URL, url, {
      method,
      maxRedirects,
      timeoutMs
    });
  } catch (error) {
    console.warn('Worker proxy failed, falling back to direct/limited check:', error.message);
    
    // Try direct fetch as final fallback
    const directResult = await checkDirect(url, {
      timeoutMs,
      maxRedirects,
      method
    });

    // If CORS blocked, fall back to limited mode
    if (directResult.corsBlocked) {
      return await checkLimited(url, {
        timeoutMs,
        enableDNS,
        thresholdMs
      });
    }

    return {
      ...directResult,
      mode: 'Direct'
    };
  }
}

async function checkViaServer(serverUrl, targetUrl, options = {}) {
  const {
    method = 'GET',
    maxRedirects = 5,
    timeoutMs = 10000,
    enableDNS = true
  } = options;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), Math.min(timeoutMs, 15000));

  try {
    const response = await fetch(serverUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        url: targetUrl,
        method,
        maxRedirects,
        timeoutMs,
        enableDNS,
        followRedirects: maxRedirects > 0
      }),
      signal: controller.signal
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `Server returned ${response.status}`);
    }

    const result = await response.json();

    // Prefer the precise httpResponseTimeMs returned by the worker
    const responseTime = (typeof result.httpResponseTimeMs === 'number')
      ? result.httpResponseTimeMs
      : result.responseTimeMs;

    return {
      mode: 'Server',
      ok: result.ok,
      httpCode: result.status || result.httpCode,
      responseTimeMs: responseTime,
      finalUrl: result.finalUrl || result.url,
      redirects: result.redirects || [],
      headers: result.headers || {},
      corsBlocked: false,
      reason: result.error || null,
      dns: result.dns || null,
      dnsResolved: result.dnsResolved || false,
      security: result.security || null,
      performance: result.performance || null,
      serverInfo: result.serverInfo || null,
      bodySnippet: result.bodySnippet || null,
      ssl: result.ssl || false
    };
  } finally {
    clearTimeout(timer);
  }
}

async function checkDirect(url, { timeoutMs = 6000, maxRedirects = 5, method = 'GET' } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  
  const redirects = [];
  let currentUrl = url;
  const start = performance.now();
  let finalResp = null;
  let httpCode = null;
  let corsBlocked = false;
  let errorReason = null;

  try {
    // keep behavior identical to previous: allow up to maxRedirects (inclusive of initial)
    for (let i = 0; i <= maxRedirects; i++) {
      const resp = await fetch(currentUrl, {
        method,
        redirect: 'manual',
        signal: controller.signal,
        cache: 'no-store',
        mode: 'cors'
      });

      httpCode = resp.status;

      if (resp.status >= 300 && resp.status < 400) {
        const loc = resp.headers.get('location');
        if (!loc) break;

        const next = new URL(loc, currentUrl).toString();
        redirects.push({
          from: currentUrl,
          to: next,
          code: resp.status
        });
        currentUrl = next;
        continue;
      } else {
        finalResp = resp;
        break;
      }
    }
  } catch (e) {
    const msg = String(e?.message || e);
    
    // Detect CORS issues - browsers often surface as TypeError
    if (e.name !== 'AbortError' && (
      e instanceof TypeError || 
      msg.includes('CORS') || 
      msg.includes('fetch') ||
      msg.includes('NetworkError') ||
      msg.includes('Failed to fetch')
    )) {
      corsBlocked = true;
    }
    
    errorReason = e.name === 'AbortError' 
      ? 'Request timeout' 
      : corsBlocked 
        ? 'CORS policy blocked this request' 
        : 'Network error';
  } finally {
    clearTimeout(timer);
  }

  const responseTimeMs = Math.round(performance.now() - start);

  if (!finalResp && (corsBlocked || errorReason)) {
    return {
      ok: false,
      corsBlocked,
      reason: errorReason,
      httpCode,
      responseTimeMs,
      finalUrl: currentUrl,
      redirects,
      headers: {}
    };
  }

  // Extract all headers
  const headers = {};
  if (finalResp) {
    for (const [key, value] of finalResp.headers.entries()) {
      headers[key] = value;
    }
  }

  return {
    ok: finalResp ? finalResp.ok : false,
    httpCode,
    responseTimeMs,
    finalUrl: currentUrl,
    redirects,
    headers,
    corsBlocked: false,
    reason: finalResp ? null : errorReason
  };
}

async function checkLimited(url, { timeoutMs, enableDNS, thresholdMs }) {
  const start = performance.now();
  const urlObj = new URL(url);
  const hostname = urlObj.hostname;
  
  let dnsResolved = false;
  let faviconReachable = false;
  let dns = null;

  try {
    // Try DNS lookup if enabled
    if (enableDNS) {
      dns = await performDNSLookup(hostname);
      dnsResolved = dns && dns.aRecords && dns.aRecords.length > 0;
    } else {
      // Assume DNS works if we can't check it
      dnsResolved = true;
    }

    // Try favicon heuristic
    const faviconResult = await checkFaviconReachability(urlObj.origin, {
      timeoutMs: Math.min(timeoutMs, 5000)
    });
    faviconReachable = faviconResult.reachable;
  } catch (error) {
    console.warn('Limited check failed:', error);
  }

  const responseTimeMs = Math.round(performance.now() - start);

  return {
    mode: 'Limited',
    ok: dnsResolved && faviconReachable,
    corsBlocked: true,
    reason: 'CORS policy blocked this request - using limited check',
    httpCode: null,
    responseTimeMs,
    finalUrl: url,
    redirects: [],
    headers: {},
    dns,
    dnsResolved,
    faviconReachable,
    limitedCheck: true
  };
}

async function checkViaProxy(proxyUrl, targetUrl, { method = 'GET', maxRedirects = 5, timeoutMs = 6000 } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(proxyUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        url: targetUrl,
        method,
        maxRedirects,
        timeoutMs,
        followRedirects: maxRedirects > 0
      }),
      signal: controller.signal
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `Proxy returned ${response.status}`);
    }

    const result = await response.json();

    // Prefer the precise httpResponseTimeMs returned by the worker/proxy when available.
    const responseTime = (typeof result.httpResponseTimeMs === 'number')
      ? result.httpResponseTimeMs
      : result.responseTimeMs;

    return {
      mode: 'Proxy',
      ok: result.ok,
      httpCode: result.status || result.httpCode,
      responseTimeMs: responseTime,
      finalUrl: result.finalUrl || result.url,
      redirects: result.redirects || [],
      headers: result.headers || {},
      corsBlocked: false,
      reason: result.error || null,
      dns: result.dns || null,
      dnsResolved: result.dnsResolved || false,
      security: result.security || null,
      performance: result.performance || null,
      serverInfo: result.serverInfo || null,
      bodySnippet: result.bodySnippet || null,
      ssl: result.ssl || false
    };
  } finally {
    clearTimeout(timer);
  }
}

function checkFaviconReachability(origin, { timeoutMs = 5000 } = {}) {
  return new Promise(resolve => {
    const icon = `${origin}/favicon.ico?t=${Date.now()}`;
    const img = new Image();
    let done = false;

    const timeout = setTimeout(() => {
      if (!done) {
        done = true;
        resolve({ reachable: false, reason: 'timeout' });
      }
    }, timeoutMs);

    img.onload = () => {
      if (!done) {
        done = true;
        clearTimeout(timeout);
        resolve({ reachable: true });
      }
    };

    img.onerror = () => {
      if (!done) {
        done = true;
        clearTimeout(timeout);
        resolve({ reachable: false, reason: 'error' });
      }
    };

    img.src = icon;
  });
}

export function classifyStatus(result, thresholdMs = 2000) {
  if (result.mode === 'Server' || result.mode === 'Proxy' || result.mode === 'Direct') {
    // Full/Server/Proxy mode classification
    if (result.reason || !result.ok) {
      if (result.httpCode) {
        if (result.httpCode >= 500) return 'Down';
        if (result.httpCode >= 400) return 'Degraded';
      }
      return 'Down';
    }

    // Check response time threshold
    if (result.responseTimeMs > thresholdMs) {
      return 'Degraded';
    }

    // Check HTTP status codes
    if (result.httpCode >= 400) {
      return result.httpCode >= 500 ? 'Down' : 'Degraded';
    }

    return 'Up';
  } else {
    // Limited mode classification
    const { dnsResolved, faviconReachable } = result;
    
    if (!dnsResolved) return 'Down';
    if (dnsResolved && faviconReachable) return 'Up';
    return 'Partial';
  }
}

async function performDNSLookup(hostname) {
  // Use Cloudflare DNS over HTTPS
  const dohUrl = `https://cloudflare-dns.com/dns-query?name=${hostname}&type=A`;
  
  try {
    const response = await fetch(dohUrl, {
      headers: {
        'Accept': 'application/dns-json'
      },
      cache: 'no-store'
    });

    if (!response.ok) throw new Error('DNS query failed');

    const data = await response.json();
    
    if (data.Answer) {
      return {
        aRecords: data.Answer
          .filter(record => record.type === 1) // A records
          .map(record => record.data),
        ttl: data.Answer[0]?.TTL || null
      };
    }

    return { aRecords: [], ttl: null };
  } catch (error) {
    throw new Error(`DNS lookup failed: ${error.message}`);
  }
}
